import { useEffect, useRef, useState } from "react";
import { Accordion, ProgressBar } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactStars from "react-stars";
import { LoadingScreen, RichTextEditor } from "Components";
import { MultiCheckDropdown } from "Components/MultiCheckDropdown";
import { authService, newAuthService } from "Service/Axios";
import {
  APPLICATION_ACTIVITY_URL,
  APPLICATION_OVERVIEW_URL,
  GET_APPLICANT_OVERVIEW_DETAILS_URL,
  GET_TASK_USER_RATING_URL,
  getAutoCompleteSearchListUrl,
  PROJECT_TABLE_USERSAVE_URL,
  SCORES_BY_TASK__URL,
  TAG_LIST_BY_TASK__URL,
  TAG_UPDATE_BY_TASK_URL,
  UPDATE_MULTIPLE_TASK_DECISION_URL,
  UPDATE_TASK_MULTISELECT_URL,
} from "Constant/api";
import {
  convertYoutubeLinkToEmbed,
  downloadFile,
  getFileNameFromUrl,
  getFirstLetterOfName,
} from "Utils";

export const Overview = ({ projectId, gid, is360View }) => {
  const { t } = useTranslation();

  const timeoutRef = useRef(null);
  const autoCompleteTimeoutRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [sectionList, setSectionList] = useState([]);
  const [actionList, setActionList] = useState([]);
  const [avarageRatingUserList, setAvarageRatingUserList] = useState([]);
  const [avarageScoreUserList, setAvarageScoreUserList] = useState([]);
  const [autoCompleteSearchList, setAutoCompleteSearchList] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [lastChanged, setLastChanged] = useState(null);

  // Listing
  const getApplicationOverview = async () => {
    const payload = {};
    if (is360View) {
      payload.userId = gid;
    } else {
      payload.gid = gid;
    }
    setLoading(true);
    const response = await newAuthService(
      is360View ? GET_APPLICANT_OVERVIEW_DETAILS_URL : APPLICATION_OVERVIEW_URL,
      "POST",
      payload
    );
    if (response.status && response.data && response.data.section) {
      const updatedResponse = response.data.section.map((section) => ({
        ...section,
        fieldDetails: section.fieldDetails.map((feild) => ({
          ...feild,
          isEditable: false,
        })),
      }));
      setSectionList(updatedResponse);
      setLoading(false);
    } else {
      setSectionList([]);
      setLoading(false);
    }
  };

  const getApplicationActivityRole = async () => {
    const data = {
      taskId: gid,
      projectId,
    };
    const response = await authService(APPLICATION_ACTIVITY_URL, "POST", data);
    setActionList(response);
  };

  useEffect(() => {
    getApplicationOverview();
    getApplicationActivityRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAvarageRatingUsersByTask = async () => {
    const data = {
      gid,
    };
    const response = await newAuthService(
      GET_TASK_USER_RATING_URL,
      "POST",
      data
    );
    if (response.status) {
      setAvarageRatingUserList(response.data);
    }
  };

  const getAvarageScoreUsersByTask = async () => {
    const data = {
      gid,
    };
    const response = await newAuthService(SCORES_BY_TASK__URL, "POST", data);
    if (response.status) {
      setAvarageScoreUserList(response.data);
    } else {
      setAvarageScoreUserList([]);
    }
  };

  const getTagListByTask = async () => {
    const data = {
      gid,
    };
    const response = await newAuthService(TAG_LIST_BY_TASK__URL, "POST", data);
    if (response.status) {
      setTagsList(response.data);
    } else {
      setTagsList([]);
    }
  };

  const getAutoCompleteSearchList = async (value) => {
    try {
      const payload = {
        searchtext: value,
        Limit: 10,
        autoSearchName: "UniversitySearch",
      };
      const response = await newAuthService(
        getAutoCompleteSearchListUrl,
        "POST",
        payload
      );
      if (response.status) {
        setAutoCompleteSearchList(response.data);
      } else {
        setAutoCompleteSearchList([]);
      }
    } catch {
      toast.error(t("common.messages.notification.failed.serverError"));
    }
  };

  // Details
  const handleEditSection = (index) => {
    const newArr = [...sectionList];
    newArr[index].isSectionEdit = true;
    newArr[index].fieldDetails = newArr[index].fieldDetails.map((feild) => ({
      ...feild,
      isEditable: feild.editable,
    }));
    setSectionList(newArr);
  };

  const handleCancelSection = () => {
    const resetList = sectionList.map((section) => ({
      ...section,
      isSectionEdit: false,
      fieldDetails: section.fieldDetails.map((field) => ({
        ...field,
        isEditable: false,
      })),
    }));
    setSectionList(resetList);
  };

  const handleChangeInput = (e, feildIndex, sectionIndex) => {
    const newArr = [...sectionList];
    newArr[sectionIndex].fieldDetails[feildIndex].value = e.target.value;
    setSectionList(newArr);

    setLastChanged({
      value: e.target.value,
      questionName: newArr[sectionIndex].fieldDetails[feildIndex].questionName,
      questionId: newArr[sectionIndex].fieldDetails[feildIndex].questionId,
    });
  };

  const handleChangeCheckbox = (e, feildIndex, sectionIndex, idx) => {
    const newArr = [...sectionList];
    const field = newArr[sectionIndex].fieldDetails[feildIndex];
    const currentItem = field.dropDownData[idx];

    if (e.target.checked) {
      field.keyvalue.push({
        key: currentItem.name,
        value: currentItem.value,
      });
    } else {
      field.keyvalue = field.keyvalue.filter(
        (item) => item.value !== currentItem.value
      );
    }

    newArr[sectionIndex].fieldDetails[feildIndex] = field;
    setSectionList(newArr);

    onUpdateMultiSelect(field.keyvalue, field.questionName, field.questionId);
  };

  const handleChangeSelect = (e, feildIndex, sectionIndex) => {
    const newArr = [...sectionList];
    newArr[sectionIndex].fieldDetails[feildIndex].value = e.target.value;
    setSectionList(newArr);
    onUpdateDetails(
      newArr[sectionIndex].fieldDetails[feildIndex].value,
      newArr[sectionIndex].fieldDetails[feildIndex].questionName,
      newArr[sectionIndex].fieldDetails[feildIndex].questionId,
      newArr[sectionIndex].fieldDetails[feildIndex].designType
    );
  };

  const handleChangeMultiSelect = (selected, feildIndex, sectionIndex) => {
    const newArr = [...sectionList];
    newArr[sectionIndex].fieldDetails[feildIndex].keyvalue = selected;
    const newValues = selected.map((select) => ({
      name: select.value,
      value: select.label,
    }));
    onUpdateMultiSelect(
      newValues,
      newArr[sectionIndex].fieldDetails[feildIndex].questionName,
      newArr[sectionIndex].fieldDetails[feildIndex].questionId
    );
  };

  const handleRatingChange = (value, feildIndex, sectionIndex) => {
    const newArr = [...sectionList];
    onUpdateDetails(
      value,
      newArr[sectionIndex].fieldDetails[feildIndex].questionName,
      newArr[sectionIndex].fieldDetails[feildIndex].questionId,
      newArr[sectionIndex].fieldDetails[feildIndex].designType
    );
  };

  const handleChangeTextEditor = (value, feildIndex, sectionIndex) => {
    const newArr = [...sectionList];
    newArr[sectionIndex].fieldDetails[feildIndex].value = value;
    setSectionList(newArr);
  };

  const handleChangeAutocomplete = (
    e,
    feildIndex,
    sectionIndex,
    autoCompleteField
  ) => {
    const newArr = [...sectionList];
    newArr[sectionIndex].fieldDetails[feildIndex].value = e.target.value;
    setSectionList(newArr);

    if (autoCompleteTimeoutRef.current) {
      clearTimeout(autoCompleteTimeoutRef.current);
    }
    autoCompleteTimeoutRef.current = setTimeout(() => {
      getAutoCompleteSearchList(e.target.value, autoCompleteField);
    }, 1000);
  };

  const handleSelectAutoComplete = (
    autoCompleteData,
    fieldData,
    sectionIndex
  ) => {
    const autoCompleteField = sectionList[sectionIndex]?.fieldDetails?.find(
      (i) =>
        i.autoCompleteMappingCheck &&
        i.autoCompleteField === fieldData.questionId
    );

    if (!autoCompleteField) {
      return;
    }

    const autoCompleteValue =
      autoCompleteData?.[autoCompleteField.autoCompleteFieldMapping] || "";

    setSectionList((prevList) => {
      const newList = [...prevList];
      newList[sectionIndex].fieldDetails = newList[
        sectionIndex
      ].fieldDetails.map((field) => {
        // ✅ Update fieldData value
        if (field.questionId === fieldData.questionId) {
          return { ...field, value: autoCompleteData.name };
        }

        // ✅ Update autoCompleteField value
        if (field.questionId === autoCompleteField.questionId) {
          return { ...field, value: autoCompleteValue };
        }

        return field;
      });

      // ✅ Call API for both fields simultaneously
      const selectedField = newList[sectionIndex].fieldDetails.find(
        (f) => f.questionId === fieldData.questionId
      );
      const mappedField = newList[sectionIndex].fieldDetails.find(
        (f) => f.questionId === autoCompleteField.questionId
      );

      if (selectedField) {
        onUpdateDetails(
          selectedField.value,
          selectedField.questionName,
          selectedField.questionId,
          selectedField.designType
        );
      }

      if (mappedField) {
        onUpdateDetails(
          mappedField.value,
          mappedField.questionName,
          mappedField.questionId,
          mappedField.designType
        );
      }

      return newList;
    });
    setAutoCompleteSearchList([]);

    if (autoCompleteTimeoutRef.current) {
      clearTimeout(autoCompleteTimeoutRef.current);
    }
  };

  const handleKeyDownInput = async (e, feildIndex, sectionIndex) => {
    if (e.key === "Enter") {
      const newArr = [...sectionList];
      onUpdateDetails(
        newArr[sectionIndex].fieldDetails[feildIndex].value,
        newArr[sectionIndex].fieldDetails[feildIndex].questionName,
        newArr[sectionIndex].fieldDetails[feildIndex].questionId,
        newArr[sectionIndex].fieldDetails[feildIndex].designType
      );
    }
  };

  // Handler
  const onUpdateDecision = async (taskDecision) => {
    const payload = {
      gid: [gid],
      decision: taskDecision,
    };
    const response = await newAuthService(
      UPDATE_MULTIPLE_TASK_DECISION_URL,
      "POST",
      payload
    );
    if (response.status && response.data.success) {
      toast.success(t("common.messages.notification.success.updateStatus"));
      getApplicationActivityRole();
    } else {
      toast.error(response.data.message);
    }
  };

  const onUpdateDetails = async (
    value,
    questionName,
    questionId,
    designType
  ) => {
    const payload = {
      gid,
      questionName,
      questionId,
      valueChange: true,
      value,
      feildType: designType,
    };
    const response = await newAuthService(
      PROJECT_TABLE_USERSAVE_URL,
      "POST",
      payload
    );
    if (response.status) {
      toast.success(t("common.messages.notification.success.updateDetails"));
      // getApplicationOverview();
    } else {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  useEffect(() => {
    if (!lastChanged) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      onUpdateDetails(
        lastChanged.value,
        lastChanged.questionName,
        lastChanged.questionId,
        lastChanged.designType
      );
    }, 5000);

    return () => clearTimeout(timeoutRef.current);
  }, [lastChanged]);

  const onUpdateMultiSelect = async (values, questionName, questionId) => {
    const payload = {
      taskId: gid,
      questionName,
      questionId,
      keyvalue: values,
    };
    if (projectId) {
      payload.projectId = projectId;
    }
    const response = await newAuthService(
      UPDATE_TASK_MULTISELECT_URL,
      "POST",
      payload
    );
    if (response.status) {
      toast.success(t("common.messages.notification.success.updateDetails"));
      // getApplicationOverview();
    } else {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  const onUpdateTags = async (e, tagIndex) => {
    const newArr = [...tagsList];
    newArr[tagIndex].checked = e.target.checked;
    setTagsList(newArr);
    const duplicateArr = newArr.map((t) => ({
      ...t,
      taskId: gid,
    }));
    await newAuthService(TAG_UPDATE_BY_TASK_URL, "POST", duplicateArr);
  };

  // Render
  const fieldDetailRender = (fieldDetail, fieldDetailIndex, sectionIndex) => {
    // console.log("designType", fieldDetail.designType);
    if (fieldDetail.designType === "rating") {
      const rating = Number(fieldDetail.value);
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label className="form-label">{fieldDetail.name}</label>
            <div
              className={`${
                avarageRatingUserList && avarageRatingUserList.length > 0
                  ? "applicant-dropdown"
                  : ""
              } position-relative`}
            >
              <div
                className="ratings"
                onMouseEnter={() => {
                  if (
                    fieldDetail?.value &&
                    !isNaN(rating) &&
                    rating > 0 &&
                    fieldDetail?.mouseHoverType === "averagerating"
                  ) {
                    getAvarageRatingUsersByTask();
                  } else {
                    setAvarageRatingUserList([]);
                  }
                }}
              >
                <ReactStars
                  count={5}
                  value={rating}
                  size={24}
                  color2={"#ffd700"}
                  edit={fieldDetail.editable}
                  onChange={(value) =>
                    handleRatingChange(value, fieldDetailIndex, sectionIndex)
                  }
                />
              </div>
              {avarageRatingUserList &&
                avarageRatingUserList.length > 0 &&
                !isNaN(rating) &&
                rating !== 0 && (
                  <div className="review-info-box card-shadow top-100 end-auto">
                    {avarageRatingUserList.map((user, index) => (
                      <div
                        className="d-flex align-items-center column-gap-1 mb-2 progress-green-900"
                        key={index.toString()}
                      >
                        <div className="img-box rounded-circle overflow-hidden">
                          {user.photoURL ? (
                            <img
                              src={user.photoURL}
                              alt={`${user.firstName}-${user.lastName}`}
                              className="w-100 h-100 object-fit-cover"
                            />
                          ) : (
                            getFirstLetterOfName(user)
                          )}
                        </div>
                        <div className="ms-auto review-info-content">
                          <div className="d-flex align-items-center column-gap-1">
                            <div className="w-100">
                              <h6 className="mb-0 text-xs">
                                {user.firstName} {user.lastName}
                              </h6>
                            </div>
                            <div className="review-list d-flex">
                              <ReactStars
                                count={5}
                                value={Number(user.rating || 0)}
                                size={16}
                                color2={"#ffd700"}
                                edit={false}
                              />
                            </div>
                          </div>
                          <div className="progressbar-div mt-2">
                            <ProgressBar
                              min={0}
                              max={5}
                              now={Number(user.rating || 0)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "hyperlink") {
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label className="form-label">{fieldDetail.name}</label>
            <a
              href={fieldDetail.value}
              target="_blank"
              rel="noopener noreferrer"
              className="line-clamp-1"
            >
              {fieldDetail.value}
            </a>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "image") {
      const fileName =
        fieldDetail.value && getFileNameFromUrl(fieldDetail.value);
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3 h-100">
            <label className="form-label">{fieldDetail.name}</label>
            <div className="overview-cover-img">
              {fieldDetail.value && (
                <>
                  <div className="position-relative overflow-hidden rounded-lg h-100">
                    {/* <span className="graph-name text-break text-xs font-medium">
                      <i className="bx bx-link-alt"></i> {fileName}
                    </span> */}
                    <div className="top-100 start-0 h-100">
                      <div className="image-overlay-applicant align-items-center d-flex justify-content-between position-absolute start-0 end-0 p-2">
                        <span className="text-break text-xs font-medium">
                          {fileName}
                        </span>
                        <div className="btn-group">
                          <button
                            type="button"
                            className="button border-0 bg-transparent"
                            onClick={() =>
                              downloadFile(fieldDetail.value, fileName)
                            }
                          >
                            <i className="bx bx-down-arrow-circle bx-sm"></i>
                          </button>
                          {/* <button
                            type="button"
                            className="button button-expand"
                            data-bs-toggle="modal"
                            data-bs-target="#graphleModal"
                          >
                            <i className="bx bx-expand-alt"></i>
                          </button> */}
                        </div>
                      </div>
                      <div className="graph-over-body h-100">
                        <img
                          src={fieldDetail.value}
                          alt={fileName}
                          className="w-100 h-100 object-fit-cover"
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className="modal fade"
                    id="graphleModal"
                    tabIndex="-1"
                    aria-labelledby="graphleModalLabel"
                    aria-hidden="true"
                  >
                    <div className="modal-dialog modal-xl modal-dialog-scrollable">
                      <div className="modal-content">
                        <button
                          type="button"
                          className="btn-close close-icon bg-alpha-0"
                          data-bs-dismiss="modal"
                          aria-label="Close"
                        >
                          <i className="bx bx-x bx-sm"></i>
                        </button>
                        <img src={fieldDetail.value} alt={fileName} />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "youtubeVideoUrl") {
      const embedLink = convertYoutubeLinkToEmbed(fieldDetail.value);
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label className="form-label">{fieldDetail.name}</label>
            <div className="ratio ratio-16x9 rounded-lg border overflow-hidden">
              <iframe
                src={embedLink}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "video") {
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label className="form-label">{fieldDetail.name}</label>
            <div className="ratio ratio-16x9 rounded-lg border overflow-hidden">
              <video controls>
                <source src={fieldDetail.value} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "tag") {
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label className="form-label">Tag</label>

            <div className="d-flex position-relative w-25">
              <div className="applicant-dropdown">
                <ul className="tag-list-inline">
                  {fieldDetail.tags?.length > 0 &&
                    fieldDetail.tags.slice(0, 3).map((tag, index) => (
                      <li key={`${tag.gid}${index}`}>
                        <span
                          type="button"
                          className="badge bg-secondary tag-badge"
                        >
                          {tag.name}
                        </span>
                      </li>
                    ))}
                  <li>
                    <button
                      className="more-tag d-flex align-items-center text-sm font-medium text-grey-800 dropdown-toggle btn-block p-0 border-0 bg-transparent"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      data-bs-auto-close="outside"
                      onClick={getTagListByTask}
                    >
                      <i className="bx bxs-plus-circle me-1"></i>{" "}
                      {t("common.button.more")}
                    </button>
                    <ul className="dropdown-menu dropdown-custom-shadow p-3 tag-dropdown">
                      <li className="search-div px-0">
                        <div className="input-group">
                          <input
                            type="search"
                            className="form-control"
                            placeholder={t("common.placeHolder.searchTags")}
                          />
                          <button type="button" className="input-group-text">
                            <i className="pluto-search"></i>
                          </button>
                        </div>
                      </li>
                      <li>
                        <div className="option-list">
                          {tagsList &&
                            tagsList.length > 0 &&
                            tagsList.map((tag, tagIndex) => (
                              <div
                                className="form-check form-check-flex mt-3"
                                key={tag.gid}
                              >
                                <label
                                  className="form-check-label me-2"
                                  htmlFor=""
                                >
                                  {tag.name}
                                </label>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={tag.checked}
                                  onChange={(e) => onUpdateTags(e, tagIndex)}
                                />
                              </div>
                            ))}
                        </div>
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "attachment") {
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label className="form-label">Files</label>
            <div className="attachment w-100 position-relative">
              <div className="data-hover w-100 text-xs">
                <i className="bx bx-link-alt"></i> 1 File
                <div className="attachment-info-box card-shadow top-100 start-0">
                  <ul className="attachment-list">
                    <li>
                      <a
                        href="https://facultydocs.s3.eu-central-1.amazonaws.com/127588/14689181/Application.pdf.pdf?X-Amz-Algorithm=AWS4-HMAC-SHA256&amp;X-Amz-Date=20240505T153223Z&amp;X-Amz-SignedHeaders=host&amp;X-Amz-Expires=172799&amp;X-Amz-Credential=AKIA6H2Z5GIWARBEDWNS%2F20240505%2Feu-central-1%2Fs3%2Faws4_request&amp;X-Amz-Signature=d447afe35210c1c49c3672c375b7594f7967f7932cbda8ba2c86f5b5ac691d2a"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Application.pdf{" "}
                        <i className="bx bx-down-arrow-circle ms-1"></i>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "select") {
      // Step 1: Find the dependent field's value
      const parentField = sectionList[sectionIndex]?.fieldDetails?.find(
        (fd) => fd.questionId === fieldDetail.parentkeyField
      );

      const dependentFieldValue = parentField?.value || "";

      // Step 2: Build dropdown options
      let dropdownOptions = fieldDetail.dropDownData || [];

      if (fieldDetail.parentkeycheck && dependentFieldValue) {
        dropdownOptions = dropdownOptions.filter((option) =>
          option.parentkey?.includes(dependentFieldValue)
        );
      }
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label htmlFor={fieldDetail.questionId} className="form-label">
              {fieldDetail.name}
            </label>
            <select
              className="form-select"
              value={fieldDetail.value}
              disabled={!fieldDetail.isEditable}
              onChange={(e) =>
                handleChangeSelect(e, fieldDetailIndex, sectionIndex)
              }
            >
              <option value="">Select an option</option>
              {dropdownOptions.map((dropdown) => (
                <option value={dropdown.value} key={dropdown.value}>
                  {dropdown.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "multiselect") {
      const optionsList =
        fieldDetail.dropDownData?.length > 0 &&
        fieldDetail.dropDownData.map((option) => ({
          value: option.value,
          label: option.name,
        }));
      const selectedValues = fieldDetail.keyvalue?.map((option) => ({
        value: option.value,
        label: option.label,
      }));
      return (
        <div
          className="col-lg-12 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label htmlFor={fieldDetail.questionId} className="form-label">
              {fieldDetail.name}
            </label>
            <MultiCheckDropdown
              options={optionsList}
              disabled={!fieldDetail.isEditable}
              value={selectedValues}
              onChange={(selected) =>
                handleChangeMultiSelect(
                  selected,
                  fieldDetailIndex,
                  sectionIndex
                )
              }
            />
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "multiinputgrid") {
      return (
        <div
          className="col-lg-12 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label htmlFor={fieldDetail.questionId} className="form-label">
              {fieldDetail.name}
            </label>
            <table
              // className="table-container"
              border="1"
              cellPadding="8"
              style={{ borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th></th>
                  {fieldDetail.gridColumns.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fieldDetail.gridRows.map((row) => (
                  <tr key={row}>
                    <td>{row}</td>
                    {fieldDetail.gridColumns.map((col) => {
                      const cellKey = `${row}-${col}`;
                      const isSelected =
                        fieldDetail.gridvalues.includes(cellKey);
                      return <td key={col}>{isSelected ? "✔️" : ""}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "autocomplete") {
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label htmlFor={fieldDetail.questionId} className="form-label">
              {fieldDetail.name}
            </label>
            <input
              type="text"
              className="form-control"
              id={fieldDetail.questionId}
              value={fieldDetail.value}
              onChange={(e) =>
                handleChangeAutocomplete(
                  e,
                  fieldDetailIndex,
                  sectionIndex,
                  fieldDetail.autoCompleteField
                )
              }
              disabled={!fieldDetail.isEditable}
            />

            {autoCompleteSearchList && autoCompleteSearchList.length > 0 && (
              <ul className="institution-list z-10">
                {autoCompleteSearchList.map((item, idx) => (
                  <li key={`${item.gid}-${idx}`}>
                    <a
                      onClick={() =>
                        handleSelectAutoComplete(
                          item,
                          fieldDetail,
                          sectionIndex
                        )
                      }
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "score") {
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label htmlFor={fieldDetail.questionId} className="form-label">
              {fieldDetail.name}
            </label>
            <div
              className="applicant-dropdown position-relative"
              onMouseEnter={() => {
                if (
                  fieldDetail?.value !== null &&
                  Number(fieldDetail?.value > 0) &&
                  fieldDetail?.mouseHoverType === "averagescore"
                ) {
                  getAvarageScoreUsersByTask();
                } else {
                  setAvarageScoreUserList([]);
                }
              }}
            >
              <label
                className="oneline-ellipsis"
                htmlFor="JU12565829"
                // onDoubleClick={
                //   singleData && singleData.editable
                //     ? () => handleTableEditFeild(key, index)
                //     : null
                // }
              >
                {fieldDetail.value}
              </label>
              {avarageScoreUserList &&
                avarageScoreUserList.length > 0 &&
                fieldDetail?.value !== null &&
                Number(fieldDetail.value) > 0 && (
                  <div className="review-info-box card-shadow botom-100 end-auto">
                    {avarageScoreUserList.map((user) => (
                      <div className="d-flex align-items-center column-gap-1 mb-2 progress-green-900">
                        <div className="img-box rounded-circle overflow-hidden">
                          {user.photoURL ? (
                            <img
                              src={user.photoURL}
                              alt={`${user.firstName}-${user.lastName}`}
                              className="w-100 h-100 object-fit-cover"
                            />
                          ) : (
                            getFirstLetterOfName(user)
                          )}
                        </div>
                        <div className="w-100">
                          <h6 className="mb-0 text-xs">
                            {user.firstName} {user.lastName}
                          </h6>
                        </div>
                        <div className="py-1 px-2 bg-grey-50 rounded-1 text-xs font-semibold">
                          {fieldDetail?.value ? fieldDetail?.value : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "radio") {
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label htmlFor={fieldDetail.questionId} className="form-label">
              {fieldDetail.name}
            </label>
            <div>
              {fieldDetail.dropDownData?.length > 0 &&
                fieldDetail.dropDownData.map((item, idx) => (
                  <div
                    className="form-check form-check-inline"
                    key={idx.toString()}
                  >
                    <input
                      className="form-check-input"
                      type="radio"
                      name={item.name}
                      id={`${item.value}-${idx}`}
                      value={item.value}
                      checked={fieldDetail.value === item.value}
                      onChange={(e) =>
                        handleChangeInput(e, fieldDetailIndex, sectionIndex)
                      }
                      disabled={!fieldDetail.isEditable}
                    />
                    <label className="form-check-label" htmlFor="inlineRadio1">
                      {item.name}
                    </label>
                  </div>
                ))}
            </div>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "checkbox") {
      const selectedValues =
        fieldDetail.keyvalue?.map((item) => item.value) || [];
      return (
        <div
          className="col-lg-12 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3 d-flex flex-column">
            <label htmlFor={fieldDetail.questionId} className="form-label">
              {fieldDetail.name}
            </label>
            <div className="d-inline-flex flex-wrap">
              {fieldDetail.dropDownData?.length > 0 &&
                fieldDetail.dropDownData.map((item, idx) => (
                  <div
                    className="form-check form-check-inline"
                    key={idx.toString()}
                  >
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`${item.value}-${idx}`}
                      value={item.value}
                      checked={selectedValues.includes(item.value)}
                      onChange={(e) =>
                        handleChangeCheckbox(
                          e,
                          fieldDetailIndex,
                          sectionIndex,
                          idx
                        )
                      }
                      disabled={!fieldDetail.isEditable}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`${item.value}-${idx}`}
                    >
                      {item.name}
                    </label>
                  </div>
                ))}
            </div>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "download") {
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label htmlFor="UploadAcademicTranscript" className="form-label">
              Please upload your most recent academic transcript
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="bx bxs-file-pdf"></i>
              </span>
              <input
                type="text"
                className="form-control"
                id="UploadAcademicTranscript"
              />
              <button className="btn btn-outline-light" type="button">
                <span className="material-symbols-outlined">download</span>
              </button>
            </div>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "textarea") {
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label htmlFor={fieldDetail.questionId} className="form-label">
              {fieldDetail.name}
            </label>
            <textarea
              className="form-control"
              id={fieldDetail.questionId}
              rows="3"
              value={fieldDetail.value}
              onChange={(e) =>
                handleChangeInput(e, fieldDetailIndex, sectionIndex)
              }
              onKeyDown={(e) =>
                handleKeyDownInput(e, fieldDetailIndex, sectionIndex)
              }
              disabled={!fieldDetail.isEditable}
            ></textarea>
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "texteditor") {
      return (
        <div
          className="col-lg-12 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label htmlFor={fieldDetail.questionId} className="form-label">
              {fieldDetail.name}
            </label>
            <RichTextEditor
              value={fieldDetail.value}
              onChange={(value) =>
                handleChangeTextEditor(value, fieldDetailIndex, sectionIndex)
              }
              readOnly={!fieldDetail.isEditable}
            />
          </div>
        </div>
      );
    } else if (fieldDetail.designType === "signature") {
      return (
        <div className="col-lg-6 col-md-12 col-sm-12 col-12">
          <div className="mb-3 rounded-lg overflow-hidden">
            <label htmlFor={fieldDetail.questionId} className="form-label">
              {fieldDetail.name}
            </label>
            <div>
              <img
                src={fieldDetail.value}
                alt="Signature"
                className="signature-image w-100"
              />
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div
          className="col-lg-6 col-md-12 col-sm-12 col-12"
          key={fieldDetailIndex.toString()}
        >
          <div className="mb-3">
            <label htmlFor={fieldDetail.questionId} className="form-label">
              {fieldDetail.name}
            </label>
            <input
              type={fieldDetail.designType ?? "text"}
              className="form-control"
              id={fieldDetail.questionId}
              value={fieldDetail.value}
              onChange={(e) =>
                handleChangeInput(e, fieldDetailIndex, sectionIndex)
              }
              onKeyDown={(e) =>
                handleKeyDownInput(e, fieldDetailIndex, sectionIndex)
              }
              disabled={!fieldDetail.isEditable}
            />
          </div>
        </div>
      );
    }
  };

  return (
    <div className="applicant-status-body overview-tab bg-transparent">
      <div className="py-2 px-5 d-flex align-items-center justify-content-center">
        {/* <h3 className="h6 font-semibold text-grey-700 mb-0">Take Action</h3> */}
        <div className="action-btn d-inline-flex flex-wrap gap-1">
          {actionList?.length > 0 &&
            actionList.map((action, index) => (
              <button
                className="btn"
                key={index.toString()}
                style={{
                  color: action.fontColor,
                  backgroundColor: action.backgroundColor,
                }}
                onClick={() => onUpdateDecision(action.name)}
              >
                {action.name}
              </button>
            ))}
        </div>
      </div>
      <div className="">
        {/* <Header gid={gid} />
        <div className="p-4 border">
          <h1>Write your code here</h1>
        </div> */}

        {loading ? (
          <div className="d-flex justify-content-center align-items-center">
            <LoadingScreen />
          </div>
        ) : (
          <Accordion
            defaultActiveKey="0"
            className="accordion-style-two bg-transparent"
          >
            {sectionList &&
              sectionList.length > 0 &&
              sectionList.map((section, index) => (
                <Accordion.Item
                  eventKey={index.toString()}
                  className=""
                  key={index.toString()}
                >
                  <Accordion.Header>{section.name}</Accordion.Header>
                  <Accordion.Body className="position-relative pt-0">
                    {section.isSectionEdit ? (
                      <button
                        className="ms-2 py-1-5 px-2 rounded-md border border-grey-200 bg-grey-100 position-absolute details-edit-btn"
                        onClick={() => handleCancelSection()}
                      >
                        <i className="bx bx-undo bx-xs"></i>
                      </button>
                    ) : (
                      <button
                        className="ms-2 py-1-5 px-2 rounded-md border border-grey-200 bg-grey-100 position-absolute details-edit-btn"
                        onClick={() => handleEditSection(index)}
                      >
                        <i className="bx bxs-pencil bx-xs"></i>
                      </button>
                    )}
                    <div className="row">
                      {section.fieldDetails &&
                        section.fieldDetails.length > 0 &&
                        section.fieldDetails.map(
                          (fieldDetail, fieldDetailIndex) =>
                            fieldDetailRender(
                              fieldDetail,
                              fieldDetailIndex,
                              index
                            )
                        )}
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
          </Accordion>
        )}
      </div>
    </div>
  );
};
